import { Vue, Component } from 'vue-property-decorator';
import AIDomainCard from '@/components/cards/ai-domain-card/ai-domain-card.vue';
import moment from 'moment';
import { competencyAPI } from '@/providers/apis/competency/competency';
import { taxonomyAPI } from '@/providers/apis/taxonomy/taxonomy';
import axios from 'axios';
import { searchAPI } from '@/providers/apis/search/search';
import { appConfigService } from '@/providers/services/app/app-config';

@Component({
    name: 'ai-generator-panel',
    components: {
        'domain-card': AIDomainCard,
    },
})
export default class AIGeneratorPanel extends Vue {

    public get learnerId() {
        return appConfigService.getAIPlayground();
    }

    // -------------------------------------------------
    // Properties

    public chartData: any = [];

    public panelTitle: any = null;

    public generateBtn = [
        {
            name: 'Generate Lesson Plans',
            key: 'lessonplan',
            label: 'Lesson Plans',
        },
        {
            name: 'Generate Quiz',
            key: 'quiz',
            label: 'Quiz',
        },
        {
            name: 'Generate Trivia',
            key: 'trivia',
            label: 'Trivia',
        },
    ];

    public generatedContent: any = [];

    public subjectCode: string = 'K12.MA';

    public fwCode: string = 'CCSS';

    public selectedItems: any = null;

    public isLoading: boolean = false;


    // -------------------------------------------------
    // Actions


    public onGenerate(item: any) {
       this.generateAIContent(item);
    }

    public onGoBack() {
        this.$router.push('/ai-playground');
    }

    public onSelectLevel(domain: any, topic: any, competency: any) {
        this.selectedItems = {
            domain,
            topic,
            competency,
        };
    }

    // -------------------------------------------------
    // Hooks

    public created() {
        this.onLoadData();
    }

    // -------------------------------------------------------------
    // Methods

    public onLoadData() {
        axios.all([
            this.fetchUserDomainCompetencyMatrix(),
            this.getSubjectDomainTopicMetadata(),
            this.fetchCrosswalk(),
        ]).then(axios.spread((domainCompetencyMatrix: any[],
                              domainTopicMetadata: any[], crossWalk: any) => {
            this.parseDomainTopicCompetencyMatrix(domainCompetencyMatrix,
                 domainTopicMetadata, crossWalk );
        }));
    }

    public fetchUserDomainCompetencyMatrix() {
        const month = moment().format('MM');
        const year = moment().format('YYYY');
        const params = {
          user: this.learnerId,
          subject: 'K12.MA',
          month,
          year,
        };
        return competencyAPI.fetchDomainTopicCompetencyMatrix(params);
      }

      public getSubjectDomainTopicMetadata() {
        const params = {
          subject: this.subjectCode,
        };
        return competencyAPI.fetchSubjectDomainTopicMetadata(params);
      }


      public generateAIContent(item: any) {
        this.isLoading = true;
        const  { domain, topic, competency } = this.selectedItems;
        this.panelTitle = null;
        let contentTitle: any = null;
        this.generatedContent = [];
        const params: any = {
            learnerId: this.learnerId,
            subjectCode: this.subjectCode,
            contentType: item.key,
            language: 'english',
            fwCode: this.fwCode,
        };
        if (topic) {
            params.topicCode = topic.topicCode;
            contentTitle = `topic "${topic.topicName}"`;
        }
        if (competency) {
            params.fwCompetencyCode = competency.frameworkCompetencyCode;
            contentTitle = `competency "${competency.competencyName}"`;
        }
        searchAPI.fetchGeneratorAI(params).then((generatedContent) => {
           this.generatedContent = generatedContent;
           (this.$refs.generatedContent as HTMLElement).scrollIntoView({behavior: 'smooth'});
           this.panelTitle = `${item.label} for the ${contentTitle}`;
           this.isLoading = false;
        });
      }

      private fetchCrosswalk() {
        const params = {
          subjectCode: this.subjectCode,
          fwCode: this.fwCode,
        };
        return taxonomyAPI.fetchCrosswalk(params);
      }

      private parseDomainTopicCompetencyMatrix(domainTopicMatrix: any[],
                                               domainTopicMetadata: any[], crossWalkData: any) {

        const chartData: any = [];
        let totalCompetencies = 0;
        domainTopicMetadata.map((domainMetadata: any) => {
          const crossWalkDomain: any = crossWalkData.find((cwDomain: any) => {
              return cwDomain.domainCode === domainMetadata.domainCode;
          });
          const domainMatrix = domainTopicMatrix.find(
            (matrix: any) => matrix.domainCode === domainMetadata.domainCode);
          const parsedDomainData = { ...domainMetadata, ...domainMatrix };

          if (domainMatrix && domainMatrix.topics) {
            const parsedDomainTopicData: any[] = [];
            domainMatrix.topics.map((topicMatrix: any) => {
              const topicMetadata = domainMetadata.topics.find(
                (topic: any) => topic.topicCode === topicMatrix.topicCode);
              const cwTopic = crossWalkDomain && crossWalkDomain.topics.find(
                (cwTopicItem: any) => cwTopicItem.topicCode === topicMatrix.topicCode);
              if (topicMatrix.competencies) {
                topicMatrix.competencies.map((competency: any) => {
                  const cwCompetency = cwTopic && cwTopic.competencies.find(
                    (cwComp: any) => cwComp.competencyCode === competency.competencyCode);
                  totalCompetencies++;
                  competency.domainCode = domainMetadata.domainCode,
                    competency.domainSeq = domainMetadata.domainSeq,
                    competency.topicCode = topicMatrix.topicCode,
                    competency.topicSeq = topicMatrix.topicSeq,
                    competency.competencyStatus = competency.status;
                  competency.isMastered = competency.status > 1;
                  competency.isInferred = competency.status === 2 || competency.status === 3;
                  competency.isSkyLineCompetency = false;
                  competency.isGradeBoundary = false;
                  competency.competencyDisplayCode = cwCompetency ? cwCompetency.frameworkCompetencyDisplayCode : null;
                  competency.frameworkCompetencyCode = cwCompetency ? cwCompetency.frameworkCompetencyCode : null;
                });
                topicMatrix.competencies[0].isSkyLineCompetency = true;
              }

              parsedDomainTopicData.push({ ...topicMatrix, ...topicMetadata });
            });
            parsedDomainData.topics = parsedDomainTopicData;
          }
          parsedDomainData.masteredCompetencies = (parsedDomainData.masteredCompetencies || 0)
                                        + (parsedDomainData.inferredCompetencies || 0);

          chartData.push(parsedDomainData);
        });
        this.chartData = chartData;
      }
}
